type AgingClass = 'fresh' | 'ok' | 'stale' | 'overdue';

interface AgingBadgeProps {
    days: number;
    /** Render in neutral gray with no overdue/stale escalation — for statuses
     *  that are long-running by design (e.g. Treatment in progress). */
    neutral?: boolean;
}

function getAgingClass(days: number): AgingClass {
    if (days === 0) return 'fresh';
    if (days <= 3) return 'ok';
    if (days <= 7) return 'stale';
    return 'overdue';
}

function getAgingLabel(days: number): string {
    if (days === 0) return 'Today';
    if (days === 1) return '1 day';
    return `${days}d`;
}

const DOT_COLOR: Record<AgingClass, string> = {
    fresh: 'bg-green-400',
    ok: 'bg-yellow-400',
    stale: 'bg-orange-400',
    overdue: 'bg-red-600'
};

const BAR_COLOR: Record<AgingClass, string> = {
    fresh: 'bg-green-400',
    ok: 'bg-yellow-400',
    stale: 'bg-orange-400',
    overdue: 'bg-red-600'
};

const TEXT_CLASS: Record<AgingClass, string> = {
    fresh: 'text-gray-500 font-normal',
    ok: 'text-gray-500 font-normal',
    stale: 'text-gray-500 font-normal',
    overdue: 'text-red-600 font-semibold'
};

export function AgingBadge({ days, neutral = false }: AgingBadgeProps) {
    const cls = getAgingClass(days);
    const barWidth = Math.min(days / 14, 1) * 100;

    // Long-running statuses render gray with no escalation: the duration is
    // informational, not an SLA warning.
    const dotColor = neutral ? 'bg-gray-300' : DOT_COLOR[cls];
    const barColor = neutral ? 'bg-gray-300' : BAR_COLOR[cls];
    const textClass = neutral ? 'text-gray-500 font-normal' : TEXT_CLASS[cls];

    return (
        <div className="flex items-center gap-1.5">
            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`} />
            <span className={`text-xs whitespace-nowrap ${textClass}`}>{getAgingLabel(days)}</span>
            <div className="w-9 h-0.5 bg-gray-200 rounded overflow-hidden">
                <div className={`h-full rounded ${barColor}`} style={{ width: `${barWidth}%` }} />
            </div>
        </div>
    );
}
